import {
  ParagonConfig,
  ParagonConfigClovenSettingsEnum,
  ParagonConfigDynamicsSettingsEnum,
  ParagonConfigZohoSettingsEnum,
  ParagonPushSettings,
  ParagonSalesforceConfigSettingsEnum,
  ParagonSharedSettings,
  SalesforcePushTargetObjectsConfig,
} from "@/types/paragon-config";
import { InstalledIntegrationType } from "@/types/installedintegration";

/**
 * Default push target objects configuration for Salesforce.
 * All standard objects enabled by default.
 */
const DEFAULT_PUSH_TARGET_OBJECTS: SalesforcePushTargetObjectsConfig = {
  households: true,
  accounts: true,
  personAccounts: true,
  contacts: true,
  leads: true,
  customObjects: [],
};

/**
 * Check if an integration type is a Salesforce-based integration.
 */
function isSalesforceIntegration(
  integrationType: InstalledIntegrationType,
): boolean {
  return (
    integrationType === InstalledIntegrationType.PARAGON_SALESFORCE ||
    integrationType === InstalledIntegrationType.PARAGON_XLR8 ||
    integrationType === InstalledIntegrationType.PARAGON_PRACTIFI ||
    integrationType ===
      InstalledIntegrationType.PARAGON_SS_C_BLACKDIAMONDELEMENTS ||
    integrationType === InstalledIntegrationType.PARAGON_FSC
  );
}

/**
 * Extracts sharedSettings from a ParagonConfig configuration JSON.
 * The sharedSettings can be located at:
 * - configuration.sharedSettings (top level)
 * - configuration.allCredentials[0].config.sharedSettings (nested in credentials)
 */
export function extractParagonSharedSettings(
  configuration: unknown,
): ParagonSharedSettings | null {
  if (!configuration || typeof configuration !== "object") {
    return null;
  }

  const config = configuration as Record<string, unknown>;

  // Try top-level sharedSettings first
  if (config.sharedSettings && typeof config.sharedSettings === "object") {
    return config.sharedSettings as ParagonSharedSettings;
  }

  // Try nested in allCredentials
  if (
    Array.isArray(config.allCredentials) &&
    config.allCredentials.length > 0
  ) {
    const credential = config.allCredentials[0] as Record<string, unknown>;
    if (
      credential?.config &&
      typeof credential.config === "object" &&
      (credential.config as Record<string, unknown>).sharedSettings
    ) {
      return (credential.config as Record<string, unknown>)
        .sharedSettings as ParagonSharedSettings;
    }
  }

  return null;
}

/**
 * Gets parsed push settings from Paragon configs for a specific integration type.
 * Returns null if no config is found for the given integration type.
 *
 * @param paragonConfigs - Array of Paragon configurations
 * @param integrationType - The integration type to find settings for
 * @returns Parsed push settings or null if not found
 */
export function getParagonPushSettings(
  paragonConfigs: ParagonConfig[] | undefined,
  integrationType: InstalledIntegrationType,
): ParagonPushSettings | null {
  if (!paragonConfigs?.length) {
    return null;
  }

  const config = paragonConfigs.find((c) => c.integration === integrationType);
  if (!config) {
    return null;
  }

  const sharedSettings = extractParagonSharedSettings(config.configuration);
  if (!sharedSettings) {
    return null;
  }

  // Handle Salesforce integrations
  if (isSalesforceIntegration(integrationType)) {
    const pushTargetObjects =
      sharedSettings[ParagonSalesforceConfigSettingsEnum.PUSH_TARGET_OBJECTS] ||
      DEFAULT_PUSH_TARGET_OBJECTS;

    return {
      // Map Salesforce config to existing boolean flags for compatibility
      summariesToContacts: pushTargetObjects.contacts,
      summariesToAccounts: pushTargetObjects.accounts,
      tasksToContacts: pushTargetObjects.contacts,
      tasksToAccounts: pushTargetObjects.accounts,
      // Include the full Salesforce config for advanced filtering
      pushTargetObjects,
    };
  }

  // Handle Zoho integrations
  if (integrationType === InstalledIntegrationType.PARAGON_ZOHO) {
    return {
      summariesToContacts:
        sharedSettings[
          ParagonConfigZohoSettingsEnum.PUSH_SUMMARIES_TO_CONTACTS
        ] ?? true,
      summariesToAccounts:
        sharedSettings[
          ParagonConfigZohoSettingsEnum.PUSH_SUMMARIES_TO_ACCOUNTS
        ] ?? true,
      tasksToContacts:
        sharedSettings[ParagonConfigZohoSettingsEnum.PUSH_TASKS_TO_CONTACTS] ??
        true,
      tasksToAccounts:
        sharedSettings[ParagonConfigZohoSettingsEnum.PUSH_TASKS_TO_ACCOUNTS] ??
        true,
    };
  }

  // Handle Cloven integrations (same Paragon workflow as Zoho — shares UUIDs)
  if (integrationType === InstalledIntegrationType.PARAGON_CLOVEN) {
    return {
      summariesToContacts:
        sharedSettings[
          ParagonConfigClovenSettingsEnum.PUSH_SUMMARIES_TO_CONTACTS
        ] ?? true,
      summariesToAccounts:
        sharedSettings[
          ParagonConfigClovenSettingsEnum.PUSH_SUMMARIES_TO_ACCOUNTS
        ] ?? true,
      tasksToContacts:
        sharedSettings[
          ParagonConfigClovenSettingsEnum.PUSH_TASKS_TO_CONTACTS
        ] ?? true,
      tasksToAccounts:
        sharedSettings[
          ParagonConfigClovenSettingsEnum.PUSH_TASKS_TO_ACCOUNTS
        ] ?? true,
    };
  }

  // Handle Dynamics integrations (default)
  return {
    summariesToContacts:
      sharedSettings[
        ParagonConfigDynamicsSettingsEnum.PUSH_SUMMARIES_TO_CONTACTS
      ] ?? true,
    summariesToAccounts:
      sharedSettings[
        ParagonConfigDynamicsSettingsEnum.PUSH_SUMMARIES_TO_ACCOUNTS
      ] ?? true,
    tasksToContacts:
      sharedSettings[
        ParagonConfigDynamicsSettingsEnum.PUSH_TASKS_TO_CONTACTS
      ] ?? true,
    tasksToAccounts:
      sharedSettings[
        ParagonConfigDynamicsSettingsEnum.PUSH_TASKS_TO_ACCOUNTS
      ] ?? true,
  };
}

/**
 * Gets the contact sync enabled setting from Paragon configs for a specific integration type.
 * Returns false if no config is found or setting is not enabled.
 *
 * @param paragonConfigs - Array of Paragon configurations
 * @param integrationType - The integration type to find settings for
 * @returns Whether contact sync is enabled
 */
export function getIsContactSyncEnabled(
  paragonConfigs: ParagonConfig[] | undefined,
  integrationType: InstalledIntegrationType,
): boolean {
  if (!paragonConfigs?.length) {
    return true;
  }

  const config = paragonConfigs.find((c) => c.integration === integrationType);
  if (!config) {
    return true;
  }

  const sharedSettings = extractParagonSharedSettings(config.configuration);
  if (!sharedSettings) {
    return true;
  }

  if (integrationType === InstalledIntegrationType.PARAGON_MICROSOFTDYNAMICS) {
    return (
      sharedSettings[ParagonConfigDynamicsSettingsEnum.ENABLE_CONTACT_SYNC] ??
      true
    );
  } else if (integrationType === InstalledIntegrationType.PARAGON_ZOHO) {
    return (
      sharedSettings[ParagonConfigZohoSettingsEnum.ENABLE_CONTACT_SYNC] ?? true
    );
  } else if (integrationType === InstalledIntegrationType.PARAGON_CLOVEN) {
    return (
      sharedSettings[ParagonConfigClovenSettingsEnum.ENABLE_CONTACT_SYNC] ??
      true
    );
  } else if (integrationType === InstalledIntegrationType.PARAGON_MAXIMIZER) {
    return true;
  } else if (isSalesforceIntegration(integrationType)) {
    return (
      sharedSettings[ParagonSalesforceConfigSettingsEnum.CONTACT_SYNC] ?? true
    );
  } else if (integrationType === InstalledIntegrationType.PARAGON_EQUISOFT) {
    return true;
  }

  return true;
}
